/**
 * @fileoverview Shared lookup-value create/edit modal used by tenant and system portals.
 * @module ui-admin/lookups
 *
 * Responsibilities:
 * - Collect lookup value metadata, display artwork, and policy settings.
 * - Emit complete lookup values to the local-first caller.
 *
 * Dependencies:
 * - React form state
 * - Shared lookup-domain types and controls
 *
 * Architecture:
 * - Presentation and form state only; persistence remains with the consuming service.
 *
 * Notes:
 * - Tenant-created values can hide the inheritance policy and are always custom-owned.
 */

// ============================================================================
// LookupValueModal — SHARED Add/Edit modal for lookup values
// ----------------------------------------------------------------------------
// Canonical source. Used by both tenant-web and system-web LookupsPage.
// Do not duplicate this component per app — extend here and both consume it.
//
// Field order (intentional):
//   1. Display Name — what users see in pickers
//   2. Code         — stable machine key, auto-slugged from Display Name
//                     (user may override; locked on edit)
//   3. Description  — optional free text
//
// FK CONTRACT — READ THIS BEFORE WIRING NEW RELATIONSHIPS
// ----------------------------------------------------------------------------
// Going forward, relationships to lookup values MUST be keyed by the lookup
// document's immutable id — the Dexie `id` (which is the same value as the
// MongoDB `_id` after sync). NEVER use `code` as a foreign key on new fields:
// `code` is editable in principle, is regenerated from the Display Name on
// create, and is not guaranteed unique across lookupType boundaries.
//
// Existing code-based FKs (e.g. config.caseTypeCodes, seed JSON references)
// are left in place for backward compatibility but should migrate to id-based
// references over time.
// ============================================================================

import React, { useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { ArtworkPicker, ArtworkRenderer } from '@firesquirrel/ui-artwork';
import {
  LOOKUP_ENTITY_CATEGORIES,
  DEFAULT_LOOKUP_SCOPE_POLICY,
  normalizeLookupEntityCategories,
} from '../../../../core-domain/src/forms/index';
import type {
  LookupValue,
  LookupGroup,
  LookupGroupInfo,
  LookupDisplay,
  LookupEditPolicy,
  LookupScope,
  InheritancePolicy,
} from '../../services/lookupService';

/**
 * @process generateLookupCode
 * Steps: Trim the display name, lowercase it, replace non-alphanumeric runs with underscores, and remove edge underscores.
 * Inputs: User-facing lookup display name.
 * Outputs: Deterministic lowercase machine code.
 * Failure Handling: Empty or punctuation-only names return an empty string for the caller to validate.
 * Idempotency: REQUIRED; identical inputs always produce identical codes.
 */
export function generateLookupCode(displayName: string): string {
  return displayName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * @contract LookupValueModalProps
 * Purpose: Defines the host-provided lookup state, editing controls, and save/close callbacks for LookupValueModal.
 * Fields: Existing value, lookup type/scope, available groups/modules, capability toggles, read-only state, onClose, onSave.
 * Rules: lockCoreFields preserves core lookup data while allowing display styling changes; onSave emits the complete value.
 * Sync: The consuming host owns Dexie-first persistence and sync wakeups.
 */
export interface LookupValueModalProps {
  value: LookupValue | null;
  lookupType: string;
  scopeContext: LookupScope;
  existingCount: number;
  availableGroups: LookupGroup[];
  availableInspectionModules: Array<{ key: string; label: string }>;
  canAssignGroup?: boolean;
  /** Show the inheritance policy selector; hidden tenant creates remain custom-owned. */
  showInheritancePolicy?: boolean;
  /** Show the "System Default" toggle (system-web admin only). */
  showSystemDefaultToggle?: boolean;
  /** Show type-specific configuration fields (system-web admin only). */
  showTypeSpecificConfig?: boolean;
  /** Show AI metadata fields (system-web admin only). */
  showAiMetadata?: boolean;
  /** Show entity-category assignment fields (system-web admin only). */
  showEntityCategories?: boolean;
  /** Show inspection-module assignment fields (system-web admin only). */
  showInspectionModules?: boolean;
  /** Fully disable the code field while editing (tenant-web behavior). */
  disableCodeFieldOnEdit?: boolean;
  /**
   * Lock all core value fields (name, code, description, active, group,
   * categories, modules, config, AI metadata) so only Display Styling is
   * editable. Used by tenant-web for locked/seed values where the tenant
   * can still customize colors, style, and display without changing the
   * value itself. Core fields are preserved from the existing value on save.
   */
  lockCoreFields?: boolean;
  /** If true, disables submit + shows the reason on hover. */
  isReadOnly?: boolean;
  readOnlyReason?: string;
  onClose: () => void;
  onSave: (v: LookupValue) => void;
}

// --------------------------------------------------------------------------
// Multi-select module dropdown (was previously duplicated in both pages)
// --------------------------------------------------------------------------
function ModuleMultiSelectDropdown({
  options,
  selected,
  onChange,
  emptyText = 'No modules available',
  disabled = false,
}: {
  options: Array<{ key: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
  emptyText?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (key: string) => {
    if (disabled) return;
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };
  const selectAll = () => { if (!disabled) onChange(options.map((o) => o.key)); };
  const clearAll = () => { if (!disabled) onChange([]); };

  const label = selected.length === 0
    ? 'None selected'
    : selected.length === options.length
      ? 'All modules'
      : `${selected.length} selected`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        className={`w-full border rounded-md px-3 py-1.5 text-sm text-left flex items-center justify-between ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'hover:border-gray-400'}`}
      >
        <span className={selected.length === 0 ? 'text-gray-400' : 'text-gray-800'}>{label}</span>
        <span className="text-gray-400">▾</span>
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg max-h-56 overflow-auto">
          {options.length === 0 ? (
            <p className="text-xs text-gray-400 px-3 py-2">{emptyText}</p>
          ) : (
            <>
              <div className="flex items-center justify-between px-3 py-1.5 border-b bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                <button type="button" onClick={selectAll} className="hover:text-orange-600">Select all</button>
                <button type="button" onClick={clearAll} className="hover:text-orange-600">Clear</button>
              </div>
              {options.map((o) => (
                <label key={o.key} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.includes(o.key)}
                    onChange={() => toggle(o.key)}
                    className="w-3.5 h-3.5 text-orange-600 rounded-sm focus:ring-orange-500"
                  />
                  <span>{o.label}</span>
                </label>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Scope policy helper (kept minimal — apps may have their own richer tables)
// --------------------------------------------------------------------------
function getLookupScopePolicy(_lookupType: string) {
  return DEFAULT_LOOKUP_SCOPE_POLICY;
}

function normalizeSelectedModules(modules: unknown): string[] {
  if (Array.isArray(modules)) return modules.filter((m): m is string => typeof m === 'string');
  if (typeof modules === 'string' && modules.trim()) return [modules.trim()];
  return [];
}

// ============================================================================
// The modal
// ============================================================================
/**
 * @component LookupValueModal
 * Purpose: Create or edit lookup values through a shared form for tenant-web and system-web.
 * Props: LookupValueModalProps supplies the value, capability flags, option lists, and mutation callbacks.
 * Behavior: Maintains form state and emits a complete lookup value; Reset to Group Default clears artwork and color overrides.
 * Data Source: Local value and options supplied by the host; the component performs no API or persistence operations.
 * Constraints: Locked core fields remain unchanged; all writes are delegated to the host's local-first service.
 * Performance: Renders only the selected lookup's form and picker; no unbounded data subscriptions.
 */
export function LookupValueModal({
  value,
  lookupType,
  scopeContext,
  existingCount,
  availableGroups,
  availableInspectionModules,
  canAssignGroup = true,
  showInheritancePolicy = true,
  showSystemDefaultToggle = false,
  showTypeSpecificConfig = true,
  showAiMetadata = true,
  showEntityCategories = true,
  showInspectionModules = true,
  disableCodeFieldOnEdit = false,
  lockCoreFields = false,
  isReadOnly = false,
  readOnlyReason,
  onClose,
  onSave,
}: LookupValueModalProps) {
  const isEdit = !!value;
  const isSystemAdmin = scopeContext === 'system';

  // ── Core fields ──────────────────────────────────────────
  const [name, setName] = useState(value?.name || '');
  const [code, setCode] = useState(value?.code || '');
  // Tracks whether the user has manually edited the Code field.
  // Once true, we stop auto-slugging from the Display Name.
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(isEdit);
  const [description, setDescription] = useState(value?.description || '');

  const handleNameChange = (next: string) => {
    setName(next);
    if (!isEdit && !codeManuallyEdited) {
      setCode(generateLookupCode(next));
    }
  };
  const handleCodeChange = (next: string) => {
    setCode(next);
    setCodeManuallyEdited(true);
  };

  const [scope, setScope] = useState<LookupScope>(value?.scope || scopeContext);
  const [inheritancePolicy, setInheritancePolicy] = useState<InheritancePolicy>(
    value?.inheritancePolicy || 'custom_owned',
  );
  const effectiveInheritancePolicy: InheritancePolicy = showInheritancePolicy
    ? inheritancePolicy
    : (value?.inheritancePolicy ?? 'custom_owned');
  const [sortOrder, setSortOrder] = useState(value?.sortOrder ?? existingCount + 1);
  const [isActive, setIsActive] = useState(value?.isActive ?? true);
  const [isSystemDefault, setIsSystemDefault] = useState(value?.isSystemDefault ?? false);
  const policy = useMemo(
    () => getLookupScopePolicy(value?.lookupType || lookupType),
    [value?.lookupType, lookupType],
  );
  const [entityCategories, setEntityCategories] = useState<string[]>(
    normalizeLookupEntityCategories(value?.category),
  );
  const [inspectionModules, setInspectionModules] = useState<string[]>(
    normalizeSelectedModules(value?.inspectionModules ?? value?.requiredModules),
  );
  const [selectedGroupId, setSelectedGroupId] = useState(value?.group?.groupId || '');
  const selectedGroup = availableGroups.find((group) => group.id === selectedGroupId);
  const groupArtworkDefault = selectedGroup?.icon
    ? { artworkId: selectedGroup.icon, color: selectedGroup.color }
    : value?.group?.groupIcon
      ? { artworkId: value.group.groupIcon, color: value.group.groupColor }
      : undefined;

  // ── Edit Policy ──────────────────────────────────────────
  const [orgCanEdit, setOrgCanEdit] = useState(value?.editPolicy?.orgCanEdit ?? true);
  const [orgCanHide, setOrgCanHide] = useState(value?.editPolicy?.orgCanHide ?? true);
  const [tenantCanEdit, setTenantCanEdit] = useState(value?.editPolicy?.tenantCanEdit ?? true);
  const [tenantCanHide, setTenantCanHide] = useState(value?.editPolicy?.tenantCanHide ?? true);
  const [lockedFields, setLockedFields] = useState(value?.editPolicy?.lockedFields?.join(', ') || '');

  // ── Display styling ──────────────────────────────────────
  const [textColor, setTextColor] = useState(value?.display?.textColor || '');
  const [bgColor, setBgColor] = useState(value?.display?.backgroundColor || '');
  const [fontWeight, setFontWeight] = useState(value?.display?.fontWeight || '');
  const [fontStyle, setFontStyle] = useState(value?.display?.fontStyle || '');
  const [badgeStyle, setBadgeStyle] = useState(value?.display?.badgeStyle || '');
  const [icon, setIcon] = useState(value?.display?.icon || '');
  const [iconColor, setIconColor] = useState(value?.display?.iconColor || '');
  const [isArtworkReset, setIsArtworkReset] = useState(false);
  const [uppercase, setUppercase] = useState(value?.display?.uppercase ?? false);
  const [strikethrough, setStrikethrough] = useState(value?.display?.strikethrough ?? false);

  // ── Config (JSON) ────────────────────────────────────────
  const [configJson, setConfigJson] = useState(
    value?.config ? JSON.stringify(value.config, null, 2) : '',
  );
  const [configError, setConfigError] = useState('');

  // ── AI Metadata ──────────────────────────────────────────
  const [aiDescription, setAiDescription] = useState(value?.aiMetadata?.aiDescription || '');
  const [aiTags, setAiTags] = useState(value?.aiMetadata?.aiTags?.join(', ') || '');
  const [aiSynonyms, setAiSynonyms] = useState(value?.aiMetadata?.aiSynonyms?.join(', ') || '');

  // ── Section toggles ──────────────────────────────────────
  const [showEditPolicy, setShowEditPolicy] = useState(!!value?.editPolicy);
  const [showDisplay, setShowDisplay] = useState(!!value?.display);
  const [showConfig, setShowConfig] = useState(!!value?.config);
  const [showAi, setShowAi] = useState(
    !!(value?.aiMetadata?.aiDescription || value?.aiMetadata?.aiTags?.length || value?.aiMetadata?.aiSynonyms?.length),
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const requiresCategory = policy.enforceCategory;
  const requiresModule = policy.enforceModule;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const finalCode = (code.trim() || generateLookupCode(name)).trim();
    if (!finalCode) return;

    let parsedConfig: Record<string, unknown> | undefined;
    if (configJson.trim()) {
      try {
        parsedConfig = JSON.parse(configJson.trim());
        setConfigError('');
      } catch {
        setConfigError('Invalid JSON');
        return;
      }
    }

    setIsSubmitting(true);

    const aiMeta = showAiMetadata && (aiDescription.trim() || aiTags.trim() || aiSynonyms.trim())
      ? {
          aiDescription: aiDescription.trim() || undefined,
          aiTags: aiTags.trim() ? aiTags.split(',').map((t) => t.trim()).filter(Boolean) : undefined,
          aiSynonyms: aiSynonyms.trim() ? aiSynonyms.split(',').map((t) => t.trim()).filter(Boolean) : undefined,
        }
      : showAiMetadata ? undefined : value?.aiMetadata;

    const ep: LookupEditPolicy | undefined = (showEditPolicy && isSystemAdmin)
      ? {
          orgCanEdit: orgCanEdit || undefined,
          orgCanHide: orgCanHide || undefined,
          tenantCanEdit: tenantCanEdit || undefined,
          tenantCanHide: tenantCanHide || undefined,
          lockedFields: lockedFields.trim() ? lockedFields.split(',').map((f) => f.trim()).filter(Boolean) : undefined,
        }
      : value?.editPolicy;

    const hasDisplay = textColor || bgColor || fontWeight || fontStyle || badgeStyle || icon || iconColor || uppercase || strikethrough;
    const disp: LookupDisplay | undefined = (showDisplay && hasDisplay)
      ? {
          textColor: textColor || undefined,
          backgroundColor: bgColor || undefined,
          fontWeight: (fontWeight as LookupDisplay['fontWeight']) || undefined,
          fontStyle: (fontStyle as LookupDisplay['fontStyle']) || undefined,
          badgeStyle: (badgeStyle as LookupDisplay['badgeStyle']) || undefined,
          icon: icon || undefined,
          iconColor: iconColor || undefined,
          uppercase: uppercase || undefined,
          strikethrough: strikethrough || undefined,
        }
      : isArtworkReset ? undefined : value?.display;

    const normalizedCategory = showEntityCategories
      ? (entityCategories.length > 0 ? entityCategories : undefined)
      : value?.category;
    const normalizedInspectionModules = showInspectionModules
      ? (inspectionModules.length > 0 ? inspectionModules : undefined)
      : (value?.inspectionModules ?? value?.requiredModules);

    let groupInfo: LookupGroupInfo | undefined;
    if (selectedGroupId) {
      const grp = availableGroups.find((g) => g.id === selectedGroupId);
      if (grp) {
        groupInfo = {
          groupId: grp.id,
          groupCode: grp.code,
          groupName: grp.name,
          groupIcon: grp.icon,
          groupColor: grp.color,
          groupSortOrder: grp.sortOrder,
        };
      }
    }

    // NOTE: `id` is preserved on edit (canonical FK). On create, the caller's
    // save layer (Dexie → sync push) assigns the real MongoDB _id.
    //
    // When lockCoreFields is true (tenant-web locked/seed values), all core
    // fields are preserved from the existing value — only `display` is updated.
    const result: LookupValue = {
      ...(value as LookupValue | undefined),
      id: value?.id || `lkv-${lookupType}-${Date.now()}`,
      lookupType,
      code: lockCoreFields ? value?.code : finalCode,
      name: lockCoreFields ? value?.name : name.trim(),
      description: lockCoreFields ? value?.description : (description.trim() || undefined),
      scope: lockCoreFields ? value?.scope : scope,
      originScope: value?.originScope || scopeContext,
      inheritancePolicy: lockCoreFields ? value?.inheritancePolicy : effectiveInheritancePolicy,
      sortOrder: lockCoreFields ? value?.sortOrder : sortOrder,
      isActive: lockCoreFields ? value?.isActive : isActive,
      _deleted: value?._deleted ?? false,
      isSystemDefault: lockCoreFields ? value?.isSystemDefault : ((showSystemDefaultToggle && isSystemDefault) || undefined),
      category: lockCoreFields ? value?.category : normalizedCategory,
      inspectionModules: lockCoreFields ? value?.inspectionModules : normalizedInspectionModules,
      requiredModules: lockCoreFields ? value?.requiredModules : normalizedInspectionModules,
      group: lockCoreFields ? value?.group : groupInfo,
      editPolicy: lockCoreFields ? value?.editPolicy : ep,
      display: disp,
      config: lockCoreFields ? value?.config : (showTypeSpecificConfig ? parsedConfig : value?.config),
      aiMetadata: lockCoreFields ? value?.aiMetadata : aiMeta,
    } as LookupValue;

    onSave(result);
  };

  const inputCls = 'w-full border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-0.5';
  const sectionBtnCls = 'flex items-center justify-between w-full text-left text-xs font-semibold text-gray-700 hover:text-orange-600 py-1';

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl flex flex-col overflow-hidden max-h-[90vh]">
        <div className="px-4 py-3 border-b flex items-center justify-between bg-gray-50">
          <h3 className="font-semibold text-gray-900">{isEdit ? 'Edit Lookup Value' : 'Add Lookup Value'}</h3>
          <button aria-label="Close" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3 overflow-y-auto text-sm">

          {/* ── Locked-value banner (tenant-web display-only editing) ── */}
          {lockCoreFields && isEdit && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900">
              This value is locked. You can customize its display style (colors, icon, font) but cannot change the value itself, hide it, or remove it.
            </div>
          )}

          {/* ── Row 1: Display Name → Code → Description ───── */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Display Name</label>
              <input
                type="text"
                required
                autoFocus={!isEdit}
                readOnly={lockCoreFields}
                title="Display Name"
                className={`${inputCls} ${lockCoreFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g. Primary Contact"
              />
            </div>
            <div>
              <label className={labelCls}>Code</label>
              <input
                type="text"
                required
                disabled={isEdit && disableCodeFieldOnEdit}
                readOnly={(isEdit && !disableCodeFieldOnEdit) || lockCoreFields}
                title="Code"
                className={`${inputCls} ${isEdit || lockCoreFields ? 'bg-gray-100 text-gray-500' : ''}`}
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="auto-generated from Display Name"
              />
            </div>
          </div>
          <div>
            <label className={labelCls}>Description (Optional)</label>
            <input
              type="text"
              readOnly={lockCoreFields}
              title="Description"
              className={`${inputCls} ${lockCoreFields ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this lookup value"
            />
          </div>

          {/* ── Row 2: Inheritance, Sort, Active ───────────── */}
          <div className={`grid ${showInheritancePolicy ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
            {showInheritancePolicy && (
              <div>
                <label className={labelCls}>Inheritance Policy</label>
                <select aria-label="Inheritance policy" className={inputCls} value={inheritancePolicy} onChange={(e) => setInheritancePolicy(e.target.value as InheritancePolicy)}>
                  <option value="seed_hideable">Seed (hideable)</option>
                  <option value="locked_mandatory">Locked (mandatory)</option>
                  <option value="custom_owned">Custom (owned)</option>
                </select>
              </div>
            )}
            <div className="flex flex-col justify-end gap-1 pb-0.5">
              <label className={`flex items-center gap-1.5 ${lockCoreFields ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                <input type="checkbox" checked={isActive} disabled={lockCoreFields} onChange={(e) => setIsActive(e.target.checked)}
                  className="w-3.5 h-3.5 text-orange-600 rounded-sm focus:ring-orange-500" />
                <span className="text-xs text-gray-700">Active</span>
              </label>
              {showSystemDefaultToggle && isSystemAdmin && (
                <label className={`flex items-center gap-1.5 ${lockCoreFields ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                  <input type="checkbox" checked={isSystemDefault} disabled={lockCoreFields} onChange={(e) => setIsSystemDefault(e.target.checked)}
                    className="w-3.5 h-3.5 text-orange-600 rounded-sm focus:ring-orange-500" />
                  <span className="text-xs text-gray-700">System Default</span>
                </label>
              )}
            </div>
          </div>

          {/* ── Row 3: Group, Categories, Modules ──────────── */}
          <div className={`grid ${showEntityCategories || showInspectionModules ? 'grid-cols-3' : 'grid-cols-1'} gap-3`}>
            <div>
              <label className={labelCls}>Group (Optional)</label>
              {canAssignGroup && !lockCoreFields ? (
                <select title="Group" className={inputCls} value={selectedGroupId} onChange={(e) => setSelectedGroupId(e.target.value)}>
                  <option value="">None (ungrouped)</option>
                  {availableGroups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              ) : (
                <input type="text" readOnly title="Group" className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`}
                  value={value?.group?.groupName || 'None (ungrouped)'} />
              )}
            </div>
            {showEntityCategories && (
              <div className="col-span-3">
                <label className={labelCls}>Assigned Categories {requiresCategory ? '(Select one or more)' : '(Optional, select any that apply)'}</label>
                <div className="grid grid-cols-3 gap-2 border rounded-md p-3 bg-gray-50 max-h-40 overflow-auto">
                  {LOOKUP_ENTITY_CATEGORIES.map((option: (typeof LOOKUP_ENTITY_CATEGORIES)[number]) => {
                    const checked = entityCategories.includes(option);
                    return (
                      <label key={option} className={`flex items-center gap-2 text-sm text-gray-700 ${lockCoreFields ? 'opacity-60' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={lockCoreFields}
                          onChange={() => setEntityCategories((prev) => checked ? prev.filter((item) => item !== option) : [...prev, option])}
                          className="w-4 h-4 text-orange-600 rounded-sm focus:ring-orange-500"
                        />
                        <span>{option}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {showInspectionModules && (
              <div>
                <label className={labelCls}>Modules {requiresModule ? '(Required)' : '(Optional)'}</label>
                <ModuleMultiSelectDropdown
                  options={availableInspectionModules}
                  selected={inspectionModules}
                  onChange={setInspectionModules}
                  emptyText="No modules available"
                  disabled={lockCoreFields}
                />
              </div>
            )}
          </div>

          {/* ── Section: Edit Policy (System Admin only) ──── */}
          {isSystemAdmin && (
            <div className="border rounded-md">
              <button type="button" className={`${sectionBtnCls} px-3`} onClick={() => setShowEditPolicy(!showEditPolicy)}>
                <span>Edit Policy — Lower-Level Permissions</span>
                <span className="text-gray-400">{showEditPolicy ? '▾' : '▸'}</span>
              </button>
              {showEditPolicy && (
                <div className="px-3 pb-3 space-y-2">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider col-span-2 mt-1">Organization Level</p>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={orgCanEdit} onChange={(e) => setOrgCanEdit(e.target.checked)}
                        className="w-3.5 h-3.5 text-orange-600 rounded-sm focus:ring-orange-500" />
                      <span className="text-xs text-gray-700">Org can edit this value</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={orgCanHide} onChange={(e) => setOrgCanHide(e.target.checked)}
                        className="w-3.5 h-3.5 text-orange-600 rounded-sm focus:ring-orange-500" />
                      <span className="text-xs text-gray-700">Org can hide this value</span>
                    </label>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider col-span-2 mt-2">Tenant / Department Level</p>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={tenantCanEdit} onChange={(e) => setTenantCanEdit(e.target.checked)}
                        className="w-3.5 h-3.5 text-orange-600 rounded-sm focus:ring-orange-500" />
                      <span className="text-xs text-gray-700">Tenant can edit this value</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={tenantCanHide} onChange={(e) => setTenantCanHide(e.target.checked)}
                        className="w-3.5 h-3.5 text-orange-600 rounded-sm focus:ring-orange-500" />
                      <span className="text-xs text-gray-700">Tenant can hide this value</span>
                    </label>
                  </div>
                  <div className="mt-2">
                    <label className={labelCls}>Locked Fields (comma-separated)</label>
                    <input type="text" title="Locked Fields" className={inputCls}
                      value={lockedFields} onChange={(e) => setLockedFields(e.target.value)}
                      placeholder="e.g. code, name" />
                    <p className="text-[10px] text-gray-400 mt-0.5">Fields that lower levels cannot modify</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Section: Display Styling ───────────────────── */}
          <div className="border rounded-md">
            <button type="button" className={`${sectionBtnCls} px-3`} onClick={() => setShowDisplay(!showDisplay)}>
              <span>Display Styling</span>
              <span className="text-gray-400">{showDisplay ? '▾' : '▸'}</span>
            </button>
            {showDisplay && (
              <div className="px-3 pb-3 space-y-2">
                {(textColor || bgColor || icon) && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] text-gray-500">Preview:</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs"
                      style={{
                        color: textColor || '#374151',
                        backgroundColor: bgColor || '#F3F4F6',
                        fontWeight: fontWeight === 'bold' ? 700 : fontWeight === 'semibold' ? 600 : fontWeight === 'medium' ? 500 : 400,
                        fontStyle: fontStyle === 'italic' ? 'italic' : 'normal',
                        textTransform: uppercase ? 'uppercase' : 'none',
                        textDecoration: strikethrough ? 'line-through' : 'none',
                      }}>
                      {(icon || groupArtworkDefault?.artworkId) && (
                        <ArtworkRenderer
                          artworkId={icon || groupArtworkDefault?.artworkId || 'glyph:tag'}
                          color={iconColor || groupArtworkDefault?.color || textColor || undefined}
                          size={12}
                        />
                      )}
                      {name || 'Sample'}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className={labelCls}>Text Color</label>
                    <div className="flex gap-1">
                      <input type="color" aria-label="Text color picker" className="w-8 h-8 rounded-sm border cursor-pointer"
                        value={textColor || '#374151'} onChange={(e) => setTextColor(e.target.value)} />
                      <input type="text" title="Text color hex" className={`${inputCls} flex-1`}
                        value={textColor} onChange={(e) => setTextColor(e.target.value)} placeholder="#374151" />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Background Color</label>
                    <div className="flex gap-1">
                      <input type="color" aria-label="Background color picker" className="w-8 h-8 rounded-sm border cursor-pointer"
                        value={bgColor || '#F3F4F6'} onChange={(e) => setBgColor(e.target.value)} />
                      <input type="text" title="Background color hex" className={`${inputCls} flex-1`}
                        value={bgColor} onChange={(e) => setBgColor(e.target.value)} placeholder="#F3F4F6" />
                    </div>
                  </div>
                </div>
                <ArtworkPicker
                  value={icon || undefined}
                  color={iconColor || undefined}
                  groupDefault={groupArtworkDefault}
                  onChange={(selection) => {
                    setIcon(selection.artworkId || '');
                    setIconColor(selection.color || '');
                    setIsArtworkReset(false);
                    setShowDisplay(true);
                  }}
                  onResetToGroupDefault={() => {
                    setIcon('');
                    setIconColor('');
                    setIsArtworkReset(true);
                  }}
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Font Weight</label>
                    <select aria-label="Font weight" className={inputCls} value={fontWeight} onChange={(e) => setFontWeight(e.target.value)}>
                      <option value="">Default</option>
                      <option value="normal">Normal</option>
                      <option value="medium">Medium</option>
                      <option value="semibold">Semibold</option>
                      <option value="bold">Bold</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Badge Style</label>
                    <select aria-label="Badge style" className={inputCls} value={badgeStyle} onChange={(e) => setBadgeStyle(e.target.value)}>
                      <option value="">Default</option>
                      <option value="solid">Solid</option>
                      <option value="outline">Outline</option>
                      <option value="subtle">Subtle</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={fontStyle === 'italic'} onChange={(e) => setFontStyle(e.target.checked ? 'italic' : '')}
                      className="w-3.5 h-3.5 text-orange-600 rounded-sm focus:ring-orange-500" />
                    <span className="text-xs text-gray-700 italic">Italic</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={uppercase} onChange={(e) => setUppercase(e.target.checked)}
                      className="w-3.5 h-3.5 text-orange-600 rounded-sm focus:ring-orange-500" />
                    <span className="text-xs text-gray-700 uppercase">Uppercase</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={strikethrough} onChange={(e) => setStrikethrough(e.target.checked)}
                      className="w-3.5 h-3.5 text-orange-600 rounded-sm focus:ring-orange-500" />
                    <span className="text-xs text-gray-700 line-through">Strikethrough</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* ── Section: Config (JSON) ─────────────────────── */}
          {showTypeSpecificConfig && (
            <div className="border rounded-md">
              <button type="button" className={`${sectionBtnCls} px-3`} onClick={() => setShowConfig(!showConfig)}>
                <span>Type-Specific Config (JSON)</span>
                <span className="text-gray-400">{showConfig ? '▾' : '▸'}</span>
              </button>
              {showConfig && (
                <div className="px-3 pb-3">
                  <textarea title="Configuration JSON" className={`${inputCls} font-mono text-xs h-24 resize-y ${configError ? 'border-red-400' : ''}`}
                    value={configJson} onChange={(e) => { setConfigJson(e.target.value); setConfigError(''); }}
                    placeholder='e.g. { "allowsExtension": true, "iccGroup": "A" }' />
                  {configError && <p className="text-xs text-red-500 mt-0.5">{configError}</p>}
                  <p className="text-[10px] text-gray-400 mt-0.5">Type-specific configuration (e.g. allowsExtension for phone types, iccGroup for occupancy)</p>
                </div>
              )}
            </div>
          )}

          {/* ── Section: AI Metadata ───────────────────────── */}
          {showAiMetadata && (
            <div className="border rounded-md">
              <button type="button" className={`${sectionBtnCls} px-3`} onClick={() => setShowAi(!showAi)}>
                <span>AI Metadata</span>
                <span className="text-gray-400">{showAi ? '▾' : '▸'}</span>
              </button>
              {showAi && (
                <div className="px-3 pb-3 space-y-2">
                  <div>
                    <label className={labelCls}>AI Description</label>
                    <textarea title="AI Description" className={`${inputCls} h-14 resize-none`}
                      value={aiDescription} onChange={(e) => setAiDescription(e.target.value)}
                      placeholder="Natural language description for AI matching..." />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Tags (comma-separated)</label>
                      <input type="text" title="AI Tags" className={inputCls}
                        value={aiTags} onChange={(e) => setAiTags(e.target.value)}
                        placeholder="e.g. contact, primary" />
                    </div>
                    <div>
                      <label className={labelCls}>Synonyms (comma-separated)</label>
                      <input type="text" title="AI Synonyms" className={inputCls}
                        value={aiSynonyms} onChange={(e) => setAiSynonyms(e.target.value)}
                        placeholder="e.g. main contact, key contact" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Footer ─────────────────────────────────────── */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50">Cancel</button>
            <button
              type="submit"
              disabled={isReadOnly || isSubmitting || !name.trim()}
              title={isReadOnly ? readOnlyReason : ''}
              className="px-3 py-2 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Value'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default LookupValueModal;
